import { BaseLineItemFragment, GiftCardFragment } from '@graphql/fragments';
import { useCustomerIdToken } from '@hooks/customer';
import { formatOrder } from '@hooks/order/helpers';
import { OrderData, OrderDetails, OrderDetailsData } from '@repo/ui/src/types/order';
import { gql } from 'graphql-request';
import type { SWRConfiguration } from 'swr';
import { GraphQLRequestWithTokenProps, requestWithTokenRequired } from '@utils/graphql-request';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import useSWRImmutable from 'swr/immutable';

const CUSTOMER_ORDERS = gql`
  ${GiftCardFragment}
  ${BaseLineItemFragment}

  query CustomerOrders {
    orders {
      items {
        details {
          status
          currency
          orderNo
          creationDate
          orderTotal
          giftCertificateItems {
            ...GiftCard
          }
          productItems {
            ...BaseLineItem
          }
          url
        }
      }
    }
  }
`;

type OrderRowFields =
  | 'currency'
  | 'orderNo'
  | 'creationDate'
  | 'orderTotal'
  | 'giftCertificateItems'
  | 'status'
  | 'productItems'
  | 'url';

export type OrderRowDetails = Pick<OrderDetails, OrderRowFields>;

interface Data {
  orders: {
    items: Array<{
      details: Pick<OrderDetailsData, OrderRowFields>;
    }>;
  };
}

const fetcher = async (props: GraphQLRequestWithTokenProps) => {
  try {
    const data = await requestWithTokenRequired<Data>(props);
    return data?.orders?.items.map((item: { details: Pick<OrderDetailsData, OrderRowFields> }) =>
      formatOrder({
        details: item.details,
        recommendations: [],
      } as unknown as OrderData),
    );
  } catch (e) {
    console.error(e);
  }
};

export const useCustomerOrders = (config: SWRConfiguration = {}) => {
  const { locale } = useCurrentUrl();
  const idToken = useCustomerIdToken();

  return useSWRImmutable({ document: CUSTOMER_ORDERS, locale, idToken }, fetcher, {
    shouldRetryOnError: false,
    ...config,
  });
};
